import React, { useEffect, useState } from "react";
import api from "../../../utils/api";
import * as XLSX from "xlsx";
import {
  Card,
  Badge,
  PageHeader,
  AdminLayout,
  DateFilter,
  ResetFilterButton,
  ExportButton,
  LoadingSpinner,
  EmptyState,
  useToast,
} from "./components/AdminSharedComponents";

export const Attendance = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const toast = useToast();

  useEffect(() => {
    const fetchAttendance = async (date = "") => {
      try {
        setLoading(true);
        const response = await api.get("/api/attendance", {
          params: { date },
        });
        const records = response.data.data || [];
        setAttendanceData(records);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
        setAttendanceData([]);
        toast.error("Gagal memuat data kehadiran");
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance(selectedDate);
  }, [selectedDate]);

  const formatTime = (time) => {
    if (!time) return "Not checked out";
    const date = new Date(time);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const groupByDate = (data) => {
    return data.reduce((groups, entry) => {
      const date = entry.check_in.split("T")[0];
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(entry);
      return groups;
    }, {});
  };

  const sortedData = [...attendanceData].sort((a, b) => {
    const dateA = new Date(a.check_in).getTime();
    const dateB = new Date(b.check_in).getTime();
    return dateB - dateA;
  });

  const groupedData = groupByDate(sortedData);

  const handleResetDateFilter = () => {
    setSelectedDate("");
  };

  const exportToExcel = () => {
    if (attendanceData.length === 0) {
      alert("No data available to export.");
      return;
    }

    let fileName = "";

    if (selectedDate) {
      fileName = `attendance_${selectedDate}.xlsx`;

      const filteredData = attendanceData.filter((entry) =>
        entry.check_in.startsWith(selectedDate)
      );

      const sheetData = filteredData.map((entry) => ({
        "Employee Name": entry.Employee.name,
        "Check-In": formatTime(entry.check_in),
        "Check-Out": formatTime(entry.check_out),
        Status: entry.status,
        Tanggal: entry.check_in.split("T")[0],
      }));

      const ws = XLSX.utils.json_to_sheet(sheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `Attendance_${selectedDate}`);
      XLSX.writeFile(wb, fileName);
    } else {
      fileName = `attendance.xlsx`;

      const sheetData = attendanceData.map((entry) => ({
        "Employee Name": entry.Employee.name,
        "Check-In": formatTime(entry.check_in),
        "Check-Out": formatTime(entry.check_out),
        Status: entry.status,
        Tanggal: entry.check_in.split("T")[0],
      }));

      const ws = XLSX.utils.json_to_sheet(sheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Attendance");
      XLSX.writeFile(wb, fileName);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <PageHeader
          title="Kehadiran"
          subtitle="Lacak dan kelola kehadiran karyawan"
        />
        <LoadingSpinner message="Memuat data kehadiran..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageHeader
        title="Kehadiran"
        subtitle="Lacak dan kelola kehadiran karyawan"
      />

      <Card accentColor="primary" className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div className="mb-4 md:mb-0">
            <DateFilter
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
            />
          </div>

          <div className="space-x-3 flex justify-end">
            {selectedDate && (
              <ResetFilterButton onReset={handleResetDateFilter} />
            )}

            <ExportButton
              onExport={exportToExcel}
              disabled={attendanceData.length === 0}
            />
          </div>
        </div>
      </Card>

      {Object.keys(groupedData).length > 0 ? (
        Object.keys(groupedData).map((date) => (
          <Card key={date} className="mb-6">
            <h3 className="text-lg font-bold mb-3 text-primary-dark">{date}</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold border-b text-primary-dark">
                      Employee Name
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold border-b text-primary-dark">
                      Check-In
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold border-b text-primary-dark">
                      Check-Out
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold border-b text-primary-dark">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groupedData[date].map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-800 border-b">
                        {entry.Employee.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 border-b">
                        {formatTime(entry.check_in)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 border-b">
                        {formatTime(entry.check_out)}
                      </td>
                      <td className="px-6 py-4 text-sm border-b">
                        <Badge variant="primary">{entry.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))
      ) : (
        <EmptyState
          message={
            selectedDate
              ? `Tidak ada data kehadiran tersedia untuk tanggal ${selectedDate}.`
              : "Tidak ada data kehadiran tersedia."
          }
          action={
            selectedDate
              ? {
                  label: "Reset Filter",
                  onClick: handleResetDateFilter,
                }
              : undefined
          }
        />
      )}
    </AdminLayout>
  );
};

export default Attendance;
